import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

const sortByMonth   = (u) => [...u].sort((a,b) => b.month - a.month);
const sortByAverage = (u) => [...u].sort((a,b) => b.average - a.average);
const sortByWines   = (u) => [...u].sort((a,b) => b.wines !== a.wines ? b.wines - a.wines : b.average - a.average);

function UgIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':    return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'shield':  return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'edit':    return <svg {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
        case 'invite':  return <svg {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>;
        case 'inbox':   return <svg {...p}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>;
        case 'leave':   return <svg {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
        case 'request': return <svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>;
        case 'kick':    return <svg {...p}><path d="M18 6 6 18M6 6l12 12"/></svg>;
        case 'chevron': return <svg {...p}><path d="m6 9 6 6 6-6"/></svg>;
        default: return null;
    }
}

function ConfirmModal({ text, onClose, onConfirm }) {
    const { t } = useTranslation();
    return (
        <div className="ug-modal-overlay" onClick={onClose}>
            <div className="ug-modal" onClick={e => e.stopPropagation()}>
                <p className="ug-modal-text">{text}</p>
                <div className="ug-modal-actions">
                    <button className="ug-modal-cancel" onClick={onClose}>{t('common.close')}</button>
                    <button className="ug-modal-confirm" onClick={onConfirm}>{t('common.confirm')}</button>
                </div>
            </div>
        </div>
    );
}

export default function Usuario() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [profileInfo, setProfileInfo]   = useState({ id: 0 });
    const [notifications, setNotifications] = useState({ winegangs: [] });
    const [gang, setGang]                 = useState(null);
    const [users, setUsers]               = useState([]);
    const [sortMode, setSortMode]         = useState('monthly');
    const [ready, setReady]               = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [demandSent, setDemandSent]     = useState(false);
    const [fetchError, setFetchError]     = useState(false);
    const [retryKey, setRetryKey]         = useState(0);

    useEffect(() => {
        const stored = localStorage.getItem('userLogged');
        if (stored) setProfileInfo(JSON.parse(stored));
    }, []);

    const fetchGangData = () => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${process.env.REACT_APP_API_URL}/winegang/${id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    const data = JSON.parse(this.responseText);
                    setGang(data);
                    setUsers(sortByMonth(data.users || []));
                    requestAnimationFrame(() => setReady(true));
                } else {
                    setFetchError(true);
                }
            }
        };
        xhr.send();
    };

    useEffect(() => {
        if (!profileInfo.id) return;
        fetchGangData();
        const xhrN = new XMLHttpRequest();
        xhrN.open('GET', `${process.env.REACT_APP_API_URL}/hasNotifications/${profileInfo.id}`, true);
        xhrN.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200)
                setNotifications(JSON.parse(this.responseText));
        };
        xhrN.send();
    }, [profileInfo.id, retryKey]);

    useEffect(() => {
        if (sortMode === 'monthly')  setUsers(prev => sortByMonth([...prev]));
        if (sortMode === 'general')  setUsers(prev => sortByAverage([...prev]));
        if (sortMode === 'wines')    setUsers(prev => sortByWines([...prev]));
    }, [sortMode]);

    const isAdmin  = gang && Number(gang.admin_user_id) === Number(profileInfo.id);
    const isMember = gang?.users?.some(u => Number(u.id) === Number(profileInfo.id));
    const hasRequestNotif = notifications.winegangs?.some(wg => Number(wg.winegang_id) === Number(id));

    const kickUser = (userId) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${process.env.REACT_APP_API_URL}/deleteWinegangUser/${userId}/${gang.winegang.id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) fetchGangData();
        };
        xhr.send();
    };

    const leaveGang = () => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${process.env.REACT_APP_API_URL}/deleteWinegangUser/${profileInfo.id}/${gang.winegang.id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) navigate('/gangs');
        };
        xhr.send();
    };

    const createDemand = () => {
        const data = new FormData();
        data.append('user_id', profileInfo.id);
        data.append('winegang_id', gang.winegang.id);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_API_URL}/demand`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 201) setDemandSent(true);
        };
        xhr.send(data);
    };

    const MEDAL = ['#d4a017', '#9baab5', '#c47b32'];

    return (
        <div className={`ug-stage${ready ? ' ug-ready' : ''}`}>
            <div className="ug-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="ug-vignette" />
            <div className="ug-grain" />

            {/* Header */}
            <div className="ug-header">
                <button className="gn-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <UgIcon name="back" size={18} />
                </button>
                <div className="ug-header-brand">
                    <UgIcon name="shield" size={14} />
                </div>
                <div style={{ width: 40 }} />
            </div>

            {/* Gang identity */}
            <div className="ug-identity">
                <h1 className="ug-gang-name">{gang?.winegang?.winegang_name || '—'}</h1>
                {gang?.winegang?.text && (
                    <p className="ug-gang-desc">{gang.winegang.text}</p>
                )}
            </div>

            {/* Role-based actions */}
            <div className="ug-actions-bar">
                {isAdmin && (<>
                    <button className="ug-action-btn ug-action-edit" onClick={() => navigate(`/gangs/editar/${gang.winegang.id}`)}>
                        <UgIcon name="edit" size={14} /><span>{t('common.edit')}</span>
                    </button>
                    <button className="ug-action-btn ug-action-invite" onClick={() => navigate(`/gangs/invitar/${gang.winegang.id}`)}>
                        <UgIcon name="invite" size={14} /><span>{t('gangs.invite')}</span>
                    </button>
                    <button className="ug-action-btn ug-action-requests" onClick={() => navigate(`/gangs/solicitudes/${gang.winegang.id}`)}>
                        <div className="ug-btn-icon-wrap">
                            <UgIcon name="inbox" size={14} />
                            {hasRequestNotif && <span className="ug-notif-dot" />}
                        </div>
                        <span>{t('gangs.requests')}</span>
                    </button>
                    <button className="ug-action-btn ug-action-leave" onClick={() => setShowLeaveModal(true)}>
                        <UgIcon name="leave" size={14} /><span>{t('gangs.leave')}</span>
                    </button>
                </>)}
                {isMember && !isAdmin && (
                    <button className="ug-action-btn ug-action-leave" onClick={() => setShowLeaveModal(true)}>
                        <UgIcon name="leave" size={14} /><span>{t('gangs.leave')}</span>
                    </button>
                )}
                {!isMember && (
                    <button
                        className={`ug-action-btn ug-action-request${demandSent ? ' ug-action-sent' : ''}`}
                        onClick={createDemand}
                        disabled={demandSent}
                    >
                        <UgIcon name="request" size={14} />
                        <span>{demandSent ? t('gangs.request_sent') : t('gangs.request')}</span>
                    </button>
                )}
            </div>

            {/* Member table panel */}
            {users.length > 0 && (
                <div className="ug-panel">
                    {/* Sort bar */}
                    <div className="rk-sort-bar">
                        {[
                            { key: 'monthly', label: t('common.month') },
                            { key: 'general', label: t('common.level') },
                            { key: 'wines',   label: t('common.wines') },
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                className={`rk-sort-btn${sortMode === key ? ' rk-sort-active' : ''}`}
                                onClick={() => setSortMode(key)}
                            >
                                {label}
                                {sortMode !== key && <UgIcon name="chevron" size={11} />}
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="rk-table">
                        <div className={`rk-thead${isAdmin ? ' rk-thead-kick' : ''}`}>
                            <span>{t('common.position')}</span>
                            <span>{t('common.name')}</span>
                            <span className={sortMode === 'monthly'  ? 'rk-col-active' : ''}>{t('common.month')}</span>
                            <span className={sortMode === 'general'  ? 'rk-col-active' : ''}>{t('common.level')}</span>
                            <span className={sortMode === 'wines'    ? 'rk-col-active' : ''}>{t('common.wines')}</span>
                            {isAdmin && <span>{t('gangs.kick')}</span>}
                        </div>
                        <div className="rk-tbody">
                            {users.map((user, i) => {
                                const isTop3 = i < 3;
                                const isSelf = Number(user.id) === Number(profileInfo.id);
                                return (
                                    <div
                                        key={user.id}
                                        className={`rk-row${isTop3 ? ' rk-row-top' : ''}${isAdmin ? ' rk-row-kick' : ''}`}
                                        style={{ animationDelay: `${i * 0.04}s` }}
                                    >
                                        <span className="rk-pos">
                                            {isTop3 && <span className="rk-medal-dot" style={{ background: MEDAL[i] }} />}
                                            {i + 1}
                                        </span>
                                        <button className="rk-name ug-user-name-btn" onClick={() => navigate(`/user/${user.id}`)}>
                                            <span className="rk-username">{user.username}{isSelf ? ' ·' : ''}</span>
                                        </button>
                                        <span className={`rk-val${sortMode === 'monthly'  ? ' rk-val-active' : ''}`}>{user.month}</span>
                                        <span className={`rk-val${sortMode === 'general'  ? ' rk-val-active' : ''}`}>{user.average.toFixed(1)}%</span>
                                        <span className={`rk-val${sortMode === 'wines'    ? ' rk-val-active' : ''}`}>{user.wines}</span>
                                        {isAdmin && (
                                            <button
                                                className={`ug-kick-btn${isSelf ? ' ug-kick-disabled' : ''}`}
                                                onClick={() => !isSelf && kickUser(user.id)}
                                                disabled={isSelf}
                                                aria-label={t('gangs.kick')}
                                            >
                                                <UgIcon name="kick" size={13} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {showLeaveModal && (
                <ConfirmModal
                    text={t('gangs.leave_confirm')}
                    onClose={() => setShowLeaveModal(false)}
                    onConfirm={leaveGang}
                />
            )}

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>
                        {t('common.retry')}
                    </button>
                </div>
            )}
        </div>
    );
}
